import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, LoadingSpinner } from '../../components/atoms';
import { FormField } from '../../components/molecues/FormField';
import { DurationHmsField } from '../../components/molecues/DurationHmsField';
import { useDocumentaryActions } from '../../hooks/useDocumentaries';
import { getLanguageService } from '../../service/ServiceRegistry';
import type { Language } from '../../types/api/language';
import type { DocumentaryCreateRequest, DocumentaryType } from '../../types/api/documentary';
import { DOCUMENTARY_SUBTYPE_OPTIONS, isSubtypeApplicable } from '../../types/api/documentary';
import toast from 'react-hot-toast';

const DOCUMENTARY_TYPE_OPTIONS: { value: DocumentaryType; label: string }[] = [
  { value: 'DHARMA_SPEECH', label: 'Dharma Speech' },
  { value: 'ASTROLOGY', label: 'Astrology' },
  { value: 'INTERVIEW', label: 'Interview' },
  { value: 'LITERARY_SEMINAR', label: 'Literary Seminar' },
];

/**
 * DocumentaryCreatePage Component
 *
 * Separate page for creating documentaries with form-data upload support.
 * Features:
 * - Form with all documentary fields
 * - Thumbnail file upload
 * - Language dropdown selection
 * - Validation & error handling
 */
export const DocumentaryCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { createDocumentary } = useDocumentaryActions();
  const languageService = getLanguageService();

  const [formData, setFormData] = useState<DocumentaryCreateRequest>({
    title: '',
    description: '',
    languageId: '',
    documentaryType: 'DHARMA_SPEECH',
    documentarySubtype: null,
    releaseDate: '',
    topic: '',
    expert: '',
    duration: undefined,
  });

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Options for dropdowns
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Load languages and genres
  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoadingOptions(true);
        const langsResponse = await languageService.getLanguages({ page: 1, size: 100 });
        setLanguages(langsResponse.items);
      } catch (error) {
        console.error('Failed to load options:', error);
        toast.error('Failed to load languages and genres');
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, [languageService]);

  // Handle thumbnail file selection
  const handleThumbnailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({ ...prev, thumbnail: 'Please select an image file' }));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, thumbnail: 'File size must be less than 10MB' }));
        return;
      }
      setThumbnailFile(file);
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.thumbnail;
        return newErrors;
      });

      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleChange = (field: keyof DocumentaryCreateRequest, value: any) => {
    // If documentaryType changes, clear subtype if not applicable
    if (field === 'documentaryType') {
      const newType = value as DocumentaryType;
      if (!isSubtypeApplicable(newType)) {
        // Clear subtype if changing to non-DHARMA_SPEECH type
        setFormData((prev) => ({ 
          ...prev, 
          [field]: value,
          documentarySubtype: null  // Clear subtype
        }));
        return;
      }
    }
    
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.languageId) {
      newErrors.languageId = 'Language is required';
    }
    if (!formData.documentaryType) {
      newErrors.documentaryType = 'Documentary type is required';
    }
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }
    if (
      isSubtypeApplicable(formData.documentaryType) &&
      !formData.documentarySubtype
    ) {
      newErrors.documentarySubtype =
        'Subtype is required for Dharma Speech (determines mobile app section)';
    }
    if (formData.duration !== undefined && formData.duration < 1) {
      newErrors.duration = 'Duration must be at least 1 second when provided';
    }
    if (!thumbnailFile) {
      newErrors.thumbnail = 'Thumbnail image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await createDocumentary(formData, thumbnailFile!);
      toast.success('Documentary created successfully');
      navigate('/pages/documentaries');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create documentary');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingOptions) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Create Documentary</h1>
          <p className="text-gray-600 mt-1">Add a new documentary to the platform</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/pages/documentaries')}>
          Cancel
        </Button>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6"
      >
        {/* Basic Information */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 text-left">
            Basic Information
          </h2>

          <FormField
            label="Title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            error={errors.title}
            required
            placeholder="Enter documentary title"
          />

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Enter documentary description"
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            />
            {errors.description && (
              <p className="text-sm text-red-600 mt-1.5">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Language <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.languageId}
                onChange={(e) => handleChange('languageId', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                <option value="">Select a language</option>
                {languages
                  .filter((lang) => lang.isActive)
                  .map((lang) => (
                    <option key={lang.languageId} value={lang.languageId}>
                      {lang.name} ({lang.nameMm})
                    </option>
                  ))}
              </select>
              {errors.languageId && (
                <p className="text-sm text-red-600 mt-1.5">{errors.languageId}</p>
              )}
            </div>

            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Documentary Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.documentaryType}
                onChange={(e) => handleChange('documentaryType', e.target.value as DocumentaryType)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                {DOCUMENTARY_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.documentaryType && (
                <p className="text-sm text-red-600 mt-1.5">{errors.documentaryType}</p>
              )}
            </div>
          </div>

          {/* Documentary Subtype - Required for DHARMA_SPEECH */}
          {isSubtypeApplicable(formData.documentaryType) && (
            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Documentary Subtype <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.documentarySubtype || ''}
                onChange={(e) =>
                  handleChange('documentarySubtype', e.target.value || null)
                }
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                <option value="" disabled>
                  Select subtype...
                </option>
                {DOCUMENTARY_SUBTYPE_OPTIONS.map((option) => (
                  <option key={option.value || ''} value={option.value || ''}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.documentarySubtype && (
                <p className="text-sm text-red-600 mt-1.5">{errors.documentarySubtype}</p>
              )}
              <p className="text-xs text-gray-500 mt-1.5">
                Determines which section the documentary appears in on the mobile app
                (တရားတော် or သုတ္တန်).
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              label="Release Date"
              type="date"
              value={formData.releaseDate || ''}
              onChange={(e) => handleChange('releaseDate', e.target.value)}
            />

            <DurationHmsField
              label="Duration"
              valueSeconds={formData.duration ?? undefined}
              onSecondsChange={(sec) => handleChange('duration', sec)}
              required={false}
              error={errors.duration}
              helperText="Optional. Sent as total seconds."
              idPrefix="doc-create-duration"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Topic"
              value={formData.topic || ''}
              onChange={(e) => handleChange('topic', e.target.value)}
              placeholder="Enter main topic (e.g. Mindfulness, History)"
            />

            <FormField
              label="Expert / Speaker"
              value={formData.expert || ''}
              onChange={(e) => handleChange('expert', e.target.value)}
              placeholder="Enter expert or speaker name"
            />
          </div>
        </div>

        {/* Thumbnail Upload */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 text-left">
            Thumbnail
          </h2>

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Thumbnail Image <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailChange}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            />
            {errors.thumbnail && (
              <p className="text-sm text-red-600 mt-1.5">{errors.thumbnail}</p>
            )}
            <p className="text-sm text-gray-500 mt-1.5">
              JPEG, PNG, or WebP. Max 10MB. Will be auto-uploaded to storage.
            </p>

            {thumbnailPreview && (
              <div className="mt-4">
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail preview"
                  className="w-48 h-64 object-cover rounded-lg border border-gray-300"
                />
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/pages/documentaries')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting}>
            Create Documentary
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DocumentaryCreatePage;

